import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import {
  emailSchema,
  passwordSchema,
  platformUserDeleteInput,
  type PlatformUserResetResult,
  type PlatformUserRow,
} from '@barbeiropro/contracts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Field } from '@/components/shared/field';
import { FormModal } from '@/components/shared/form-modal';
import { api, errorMessage, HttpError } from '@/lib/api';

/** Mesma máscara do backend (maskEmail): 2 primeiras letras + "***@domínio". */
export const maskEmail = (email: string) => {
  const [user = '', domain = ''] = email.split('@');
  return `${user.slice(0, 2)}***@${domain}`;
};

function useInvalidateUsers() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['platform', 'users'] });
}

const FOOTER_BTN = 'flex-1 sm:flex-none';

// ---------- editar ----------
const editSchema = z.object({
  name: z.string().trim().min(2, 'Informe o nome').max(120, 'Máximo de 120 caracteres'),
  email: emailSchema,
});
type EditValues = z.input<typeof editSchema>;

export function EditUserModal({ user, memberships, onClose }: { user: PlatformUserRow; memberships: PlatformUserRow[]; onClose: () => void }) {
  const invalidate = useInvalidateUsers();
  const orgs = memberships.filter((m) => m.organizationId);
  const [active, setActive] = useState<Record<string, boolean>>(() => Object.fromEntries(orgs.map((m) => [m.organizationId!, m.membershipActive !== false])));
  const form = useForm<EditValues>({ resolver: zodResolver(editSchema), defaultValues: { name: user.name, email: user.email } });
  const save = useMutation({
    mutationFn: async (v: z.output<typeof editSchema>) => {
      const body: Record<string, unknown> = {};
      if (v.name !== user.name) body.name = v.name;
      if (v.email.toLowerCase() !== user.email.toLowerCase()) body.email = v.email;
      if (Object.keys(body).length) await api.patch(`/platform/users/${user.userId}`, body);
      for (const m of orgs) {
        if (active[m.organizationId!] !== (m.membershipActive !== false)) {
          await api.patch(`/platform/users/${user.userId}`, { active: active[m.organizationId!], organizationId: m.organizationId });
        }
      }
    },
    onSuccess: () => {
      toast.success('Usuário atualizado');
      void invalidate();
      onClose();
    },
    onError: (e) => {
      if (e instanceof HttpError && e.status === 409 && e.code === 'email_taken') form.setError('email', { message: 'Este e-mail já está em uso por outra conta' });
      else toast.error(errorMessage(e));
    },
  });
  return (
    <FormModal
      open
      onOpenChange={(v) => !v && onClose()}
      title="Editar usuário"
      description="Trocar o e-mail zera a verificação. Desativar um vínculo encerra as sessões do usuário."
      size="sm"
      footer={
        <>
          <Button variant="outline" className={FOOTER_BTN} onClick={onClose}>Cancelar</Button>
          <Button type="submit" form="user-edit-form" className={FOOTER_BTN} disabled={save.isPending}>{save.isPending ? 'Salvando…' : 'Salvar'}</Button>
        </>
      }
    >
      <form id="user-edit-form" noValidate className="grid gap-4" onSubmit={form.handleSubmit((v) => save.mutate(editSchema.parse(v)))}>
        <Field id="usr-name" label="Nome" error={form.formState.errors.name?.message}>
          <Input id="usr-name" autoComplete="off" {...form.register('name')} />
        </Field>
        <Field id="usr-email" label="E-mail" error={form.formState.errors.email?.message}>
          <Input id="usr-email" type="email" autoComplete="off" {...form.register('email')} />
        </Field>
        {orgs.length > 0 && (
          <div className="grid gap-3">
            <p className="text-sm font-medium">Situação por barbearia</p>
            {orgs.map((m) => (
              <Field key={m.organizationId} id={`usr-active-${m.organizationId}`} label={`${m.organizationName} · ${m.roleName ?? '—'}`}>
                <Select value={active[m.organizationId!] ? 'active' : 'inactive'} onValueChange={(v) => setActive((a) => ({ ...a, [m.organizationId!]: v === 'active' }))}>
                  <SelectTrigger id={`usr-active-${m.organizationId}`} className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Ativo</SelectItem>
                    <SelectItem value="inactive">Inativo</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            ))}
          </div>
        )}
      </form>
    </FormModal>
  );
}

// ---------- definir senha ----------
const passwordFormSchema = z
  .object({
    password: passwordSchema,
    confirm: z.string().min(1, 'Confirme a senha'),
    reason: platformUserDeleteInput.shape.reason,
  })
  .refine((v) => v.password === v.confirm, { message: 'As senhas não conferem', path: ['confirm'] });
type PasswordValues = z.input<typeof passwordFormSchema>;

export function SetPasswordModal({ user, onClose }: { user: PlatformUserRow; onClose: () => void }) {
  const invalidate = useInvalidateUsers();
  const form = useForm<PasswordValues>({ resolver: zodResolver(passwordFormSchema), defaultValues: { password: '', confirm: '', reason: '' } });
  const save = useMutation({
    mutationFn: (v: PasswordValues) => api.post(`/platform/users/${user.userId}/password`, { password: v.password, reason: v.reason }),
    onSuccess: () => {
      toast.success('Senha definida');
      void invalidate();
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const err = form.formState.errors;
  return (
    <FormModal
      open
      onOpenChange={(v) => !v && onClose()}
      title="Definir senha"
      description={`Nova senha para ${user.name}. A ação fica registrada na auditoria.`}
      size="sm"
      footer={
        <>
          <Button variant="outline" className={FOOTER_BTN} onClick={onClose}>Cancelar</Button>
          <Button type="submit" form="user-password-form" className={FOOTER_BTN} disabled={save.isPending}>{save.isPending ? 'Salvando…' : 'Definir senha'}</Button>
        </>
      }
    >
      <form id="user-password-form" noValidate className="grid gap-4" onSubmit={form.handleSubmit((v) => save.mutate(v))}>
        <p className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm" role="note">
          <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden /> Encerra todas as sessões do usuário.
        </p>
        <Field id="usr-pw" label="Nova senha" hint="Mínimo de 8 caracteres, com ao menos uma letra e um número." error={err.password?.message}>
          <Input id="usr-pw" type="password" autoComplete="new-password" {...form.register('password')} />
        </Field>
        <Field id="usr-pw2" label="Confirmar senha" error={err.confirm?.message}>
          <Input id="usr-pw2" type="password" autoComplete="new-password" {...form.register('confirm')} />
        </Field>
        <Field id="usr-pw-reason" label="Motivo" error={err.reason?.message}>
          <Textarea id="usr-pw-reason" rows={3} maxLength={300} placeholder="Ex.: usuário sem acesso ao e-mail, validado por telefone" {...form.register('reason')} />
        </Field>
      </form>
    </FormModal>
  );
}

// ---------- redefinir por link ----------
export function ResetLinkModal({ user, onClose }: { user: PlatformUserRow; onClose: () => void }) {
  const [result, setResult] = useState<PlatformUserResetResult | null>(null);
  const send = useMutation({
    mutationFn: () => api.post<PlatformUserResetResult>(`/platform/users/${user.userId}/reset-password`),
    onSuccess: (r) => {
      setResult(r);
      if (r.sent) toast.success('Link de redefinição enviado');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const done = result?.sent === true;
  return (
    <FormModal
      open
      onOpenChange={(v) => !v && onClose()}
      title="Redefinir senha (enviar link)"
      size="sm"
      footer={
        <>
          <Button variant="outline" className={FOOTER_BTN} onClick={onClose}>{done ? 'Fechar' : 'Cancelar'}</Button>
          {!done && (
            <Button className={FOOTER_BTN} disabled={send.isPending} onClick={() => send.mutate()}>{send.isPending ? 'Enviando…' : 'Enviar link'}</Button>
          )}
        </>
      }
    >
      <div className="grid gap-3 text-sm">
        <p>
          Enviaremos um link de redefinição (válido por 1 hora) para <strong className="break-all">{maskEmail(user.email)}</strong>. A senha atual continua valendo até o usuário criar outra.
        </p>
        {result && !result.sent && (
          <p className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3" role="status">
            <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden /> O envio de e-mail não está configurado neste ambiente. Nenhum link foi gerado; use “Definir senha”.
          </p>
        )}
        {result?.sent && <p role="status" className="rounded-md border bg-muted/40 p-3">Link enviado para {result.to}.</p>}
      </div>
    </FormModal>
  );
}

// ---------- excluir ----------
type DeleteValues = z.input<typeof platformUserDeleteInput>;

export function DeleteUserModal({ user, onClose }: { user: PlatformUserRow; onClose: () => void }) {
  const invalidate = useInvalidateUsers();
  const [apiError, setApiError] = useState<string | null>(null);
  const form = useForm<DeleteValues>({ resolver: zodResolver(platformUserDeleteInput), defaultValues: { reason: '' } });
  const del = useMutation({
    mutationFn: (v: DeleteValues) => api.del(`/platform/users/${user.userId}`, v),
    onSuccess: () => {
      toast.success('Usuário excluído');
      void invalidate();
      onClose();
    },
    onError: (e) => {
      if (e instanceof HttpError && e.status === 409) setApiError(e.body.message);
      else toast.error(errorMessage(e));
    },
  });
  return (
    <FormModal
      open
      onOpenChange={(v) => !v && onClose()}
      title="Excluir usuário"
      description={`${user.name} (${user.email}) perde o acesso e os dados pessoais são removidos. O histórico de operações é mantido.`}
      size="sm"
      footer={
        <>
          <Button variant="outline" className={FOOTER_BTN} onClick={onClose}>Cancelar</Button>
          <Button type="submit" form="user-delete-form" variant="destructive" className={FOOTER_BTN} disabled={del.isPending}>{del.isPending ? 'Excluindo…' : 'Excluir'}</Button>
        </>
      }
    >
      <form
        id="user-delete-form"
        noValidate
        className="grid gap-4"
        onSubmit={form.handleSubmit((v) => {
          setApiError(null);
          del.mutate(v);
        })}
      >
        {apiError && (
          <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">{apiError}</p>
        )}
        <Field id="usr-del-reason" label="Motivo" error={form.formState.errors.reason?.message}>
          <Textarea id="usr-del-reason" rows={3} maxLength={300} placeholder="Ex.: pedido de exclusão de conta feito pelo titular" {...form.register('reason')} />
        </Field>
      </form>
    </FormModal>
  );
}
